/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Dispatch, SetStateAction } from "react";
import { useParams } from "react-router-dom";
import {
  FlexContainer,
  Row,
  SVGIcon,
  Switch,
  Table,
  Text,
  theme,
  Tooltip,
} from "@nordcloud/gnui";
import { useToggle } from "~/hooks";
import { isNotEmpty, Maybe } from "~/tools";
import { StyledTd, StyledTr, SwitchWrap, TdWithMinWidth } from "../styles";
import { changeApplicationValue, changeEnvValue } from "./utils";

type Environment = {
  nid: string;
  name: string;
  orgUnitsV2?: Maybe<{
    orgUnits?: Maybe<
      {
        nid: string;
        name: string;
        businessContext: {
          id: string;
        };
      }[]
    >;
  }>;
};

type Props = {
  name: string;
  environments: Maybe<Environment>[];
  envs: Maybe<Environment>[];
  setEnvs: Dispatch<SetStateAction<Maybe<Environment>[]>>;
  envsToRemove: Maybe<Environment>[];
  setEnvsToRemove: Dispatch<SetStateAction<Maybe<Environment>[]>>;
};

type RouteParams = {
  structureId: string;
  unitId: string;
};

export function EnvSelectorRow({
  name,
  environments,
  envs,
  setEnvs,
  envsToRemove,
  setEnvsToRemove,
}: Props) {
  const { structureId = "", unitId = "" } = useParams<RouteParams>();
  const [isRowOpen, toggleIsRowOpen] = useToggle();

  const switchEnv = (inputEnv: Maybe<Environment>) => {
    changeEnvValue({
      inputEnv,
      unitId,
      envs,
      setEnvs,
      envsToRemove,
      setEnvsToRemove,
    });
  };

  const switchApp = (inputEnvs: Maybe<Environment>[]) => {
    changeApplicationValue({
      inputEnvs,
      environments,
      unitId,
      envs,
      setEnvs,
      envsToRemove,
      setEnvsToRemove,
      countSelected,
    });
  };

  const isEnvironmentSelected = (environment: Maybe<Environment>) => {
    const isAddedToEnvs = envs.some((env) => env?.nid === environment?.nid);
    const isAlreadySelected = environment?.orgUnitsV2?.orgUnits?.some(
      (ou) => ou.nid === unitId
    );
    const isNotRemoved = !envsToRemove.find(
      (env) => env?.nid === environment?.nid
    );
    return isAddedToEnvs || (isAlreadySelected && isNotRemoved);
  };

  const countSelected = environments?.filter(
    (environment: Maybe<Environment>) => isEnvironmentSelected(environment)
  )?.length;

  const hasConflict = isNotEmpty(
    environments?.filter(
      (env: Maybe<Environment>) =>
        env?.orgUnitsV2?.orgUnits?.find(
          (ou) => ou.businessContext.id === structureId && ou.nid !== unitId
        )
    ) ?? []
  );

  const hasEnvironments = isNotEmpty(environments ?? []);

  return (
    <Table.tbody>
      <Table.tr>
        <TdWithMinWidth
          data-testid="application-expand"
          onClick={toggleIsRowOpen}
        >
          {hasEnvironments && (
            <div css={{ transform: "translateY(5px)" }}>
              <SVGIcon
                name="chevronDown"
                css={{
                  transition: "transform .1s linear",
                  transform: isRowOpen ? "rotate(0deg)" : "rotate(-90deg)",
                }}
              />
            </div>
          )}
        </TdWithMinWidth>
        <Table.td>
          <FlexContainer>
            <span css={{ wordBreak: "break-all" }}>{name}</span>
            <div
              css={{
                position: "relative",
                marginLeft: theme.spacing.spacing02,
              }}
            >
              {hasConflict && (
                <Tooltip
                  caption="Environments in this application are used in other organization units."
                  status="notification"
                  minWidth="11rem"
                >
                  <SVGIcon name="info" color="notification" size="sm" />
                </Tooltip>
              )}
            </div>
          </FlexContainer>
          <Row css={{ marginLeft: 0 }}>
            <Text tag="span" size="sm" color={theme.color.text.text02}>
              {environments?.length} environment
              {environments?.length === 1 ? "" : "s"}
            </Text>
          </Row>
        </Table.td>
        <StyledTd />
        <StyledTd>
          <SwitchWrap>
            {hasEnvironments && (
              <Switch
                data-testid="application-select"
                checked={countSelected === environments?.length}
                onChange={() => {
                  switchApp(environments ?? []);
                }}
              />
            )}
          </SwitchWrap>
        </StyledTd>
      </Table.tr>

      {environments?.map((environment: Maybe<Environment>) => (
        <StyledTr key={environment?.nid} show={isRowOpen}>
          <td />
          <Table.td css={{ wordBreak: "break-all" }}>
            {environment?.name}
            <Row css={{ marginLeft: 0 }}>
              <Text tag="span" size="sm" color={theme.color.text.text02}>
                {
                  environment?.orgUnitsV2?.orgUnits?.find(
                    (ou) => ou.businessContext.id === structureId
                  )?.name
                }
              </Text>
            </Row>
          </Table.td>
          <Table.td />
          <StyledTd>
            <SwitchWrap>
              <Switch
                checked={isEnvironmentSelected(environment)}
                data-testid="environment-select"
                onChange={() => {
                  switchEnv(environment);
                }}
              />
            </SwitchWrap>
          </StyledTd>
        </StyledTr>
      ))}
    </Table.tbody>
  );
}
